import React, { createContext, useContext, useState, ReactNode } from "react";
import { MaritimeQuizState, MaritimeInterestType, MaritimePriorityType } from "@/types/quiz";

interface QuizContextType {
  quizState: MaritimeQuizState;
  toggleInterest: (interest: MaritimeInterestType) => void;
  setPriorities: (priorities: MaritimePriorityType[]) => void;
  nextStep: () => void;
  previousStep: () => void;
  resetQuiz: () => void;
}

const QuizContext = createContext<QuizContextType | undefined>(undefined);

const initialState: MaritimeQuizState = {
  interests: [],
  priorities: [],
  currentStep: 1,
};

export function QuizProvider({ children }: { children: ReactNode }) {
  const [quizState, setQuizState] = useState<MaritimeQuizState>(initialState);

  const toggleInterest = (interest: MaritimeInterestType) => {
    setQuizState((prev) => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter((i) => i !== interest)
        : [...prev.interests, interest],
    }));
  };

  const setPriorities = (priorities: MaritimePriorityType[]) => {
    setQuizState((prev) => ({ ...prev, priorities }));
  };

  const nextStep = () => {
    setQuizState((prev) => ({ ...prev, currentStep: prev.currentStep + 1 }));
  };

  const previousStep = () => {
    setQuizState((prev) => ({ ...prev, currentStep: Math.max(1, prev.currentStep - 1) }));
  };

  const resetQuiz = () => {
    setQuizState(initialState);
  };

  return (
    <QuizContext.Provider
      value={{
        quizState,
        toggleInterest,
        setPriorities,
        nextStep,
        previousStep,
        resetQuiz,
      }}
    >
      {children}
    </QuizContext.Provider>
  );
}

export function useQuiz() {
  const context = useContext(QuizContext);
  if (context === undefined) {
    throw new Error("useQuiz must be used within a QuizProvider");
  }
  return context;
}
